import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import {
  EIN_LENGTH,
  MAX_ADDRESS_LINE_LENGTH,
  MAX_ORGANIZATION_LENGTH,
  MIN_ORGANIZATION_LENGTH,
} from 'features/my-profile/become-fundraiser/context/becomeFundraiserUtils';

export interface OrganizationFormData {
  isNonProfit: boolean;
  name: string;
  ein: string;
  state: string;
  zipCode: string;
  city: string;
  country: string;
  addressLine1: string;
  addressLine2?: string;
}

export const useAddOrganizationForm = (
  onSelected: (organization: OrganizationFormData) => void,
  organization?: OrganizationFormData
) => {
  const { t } = useTranslation('myProfile');

  const validationSchema = Yup.object().shape({
    isNonProfit: Yup.boolean().required(),
    name: Yup.string()
      .trim()
      .min(MIN_ORGANIZATION_LENGTH, t('form-inputs.organization-name-too-short'))
      .max(MAX_ORGANIZATION_LENGTH)
      .required(t('form-inputs.organization-name-required')),
    ein: Yup.string()
      .trim()
      .matches(/^[\d-]+$/, t('form-inputs.not-allowed-chars'))
      .length(EIN_LENGTH, t('form-inputs.ein-invalid-length'))
      .required(t('form-inputs.ein-required')),
    state: Yup.string().trim().required(t('form-inputs.state-required')),
    zipCode: Yup.string().trim().required(t('form-inputs.zip-required')),
    city: Yup.string().trim().required(t('form-inputs.city-required')),
    country: Yup.string().trim().required(t('form-inputs.country-required')),
    addressLine1: Yup.string().trim().max(MAX_ADDRESS_LINE_LENGTH).required(t('form-inputs.address-required')),
    addressLine2: Yup.string().trim().max(MAX_ADDRESS_LINE_LENGTH),
  });

  const initialValues = {
    isNonProfit: true,
    name: organization?.name || '',
    ein: organization?.ein || '',
    state: organization?.state || '',
    zipCode: organization?.zipCode || '',
    city: organization?.city || '',
    country: organization?.country || '',
    addressLine1: organization?.addressLine1 || '',
    addressLine2: organization?.addressLine2 || undefined,
  };
  const onSubmit = (organization: OrganizationFormData) => onSelected(organization);

  return useFormik({ initialValues, validationSchema, onSubmit, validateOnMount: true });
};
